package org.example.project.enginepersonal

import org.example.project.settings.UISettings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.screens_cus.Abwesenheitsbedarf
import org.example.project.screens_cus.UrlaubsApi
import org.example.project.screens_cus.Urlaubsanspruch
import org.example.project.enginekalender.kalenderTodayIsoDate
import org.example.project.enginekalender.parseKalenderIsoDate
import org.example.project.service.GlobalManager
import org.example.project.service.ZustandManager
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.example.project.utils.UserAvatarCircle
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date

private data class UrlaubsuebersichtMonth(val year: Int, val month: Int) {
    fun shift(delta: Int): UrlaubsuebersichtMonth {
        val next = Date(year, month - 1 + delta, 1)
        return UrlaubsuebersichtMonth(next.getFullYear().toInt(), next.getMonth().toInt() + 1)
    }
}

@Composable
fun CUSUrlaubsuebersicht(art: String = "CUS") {
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("urlaub"),
        GlobalManager.jsonDomainVersion("kalender"),
        GlobalManager.jsonDomainVersion("user"),
        GlobalManager.jsonDomainVersion("personal"),
        GlobalManager.jsonDomainVersion("zeit")
    )
    val scope = rememberCoroutineScope()
    var users by remember { mutableStateOf<List<All>>(emptyList()) }
    var vacations by remember { mutableStateOf<List<All>>(emptyList()) }
    var month by remember { mutableStateOf(currentUrlaubsuebersichtMonth()) }
    var overviewMode by remember { mutableStateOf("Team") }
    var selectedUserId by remember { mutableStateOf("") }
    var singleDate by remember { mutableStateOf(kalenderTodayIsoDate()) }
    var singleDayPanelIso by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(true) }
    var savingCell by remember { mutableStateOf<String?>(null) }
    var message by remember { mutableStateOf("") }
    var allowancesByUser by remember { mutableStateOf<Map<String, Urlaubsanspruch>>(emptyMap()) }
    var loadedOnce by remember { mutableStateOf(false) }

    suspend fun reload() {
        if (!loadedOnce) loading = true
        users = UrlaubsApi.loadUsers().sortedWith(
            compareBy<All> { it.personalnummer.ifBlank { "\uFFFF" } }
                .thenBy { urlaubsuebersichtUserName(it) }
        )
        vacations = (if (overviewMode == "Team") {
            UrlaubsApi.loadMonth(month.year, month.month)
        } else {
            UrlaubsApi.loadYear(singleDate.take(4).toIntOrNull() ?: month.year)
        })
            .filter(::urlaubsuebersichtIsVisibleAbsence)
        loadedOnce = true
        loading = false
    }

    LaunchedEffect(refreshVersion, month, overviewMode, singleDate.take(4)) { reload() }
    LaunchedEffect(users) {
        if (selectedUserId.isBlank() || users.none { it.id == selectedUserId }) {
            selectedUserId = users.firstOrNull()?.id.orEmpty()
        }
    }
    LaunchedEffect(users, vacations, month.year, singleDate.take(4), overviewMode) {
        val year = if (overviewMode == "Team") month.year else singleDate.take(4).toIntOrNull() ?: month.year
        allowancesByUser = UrlaubsApi.loadAllowances(year).associateBy { it.userId }
    }

    val days = urlaubsuebersichtDaysInMonth(month.year, month.month)
    val monthPrefix = "${month.year}-${month.month.toString().padStart(2, '0')}-"
    val vacationsByCell = remember(vacations, month) {
        val cells = mutableMapOf<String, All>()
        vacations.forEach { vacation ->
            urlaubsuebersichtDisplayDays(vacation).filter { it.startsWith(monthPrefix) }.forEach { day ->
                if (urlaubsuebersichtVacationColor(vacation, day) == null) return@forEach
                val key = "${vacation.userId}|$day"
                val existing = cells[key]
                if (existing == null ||
                    kontingentAbsenceStatusPriority(vacation) > kontingentAbsenceStatusPriority(existing)
                ) {
                    cells[key] = vacation
                }
            }
        }
        cells
    }

    Style(UrlaubsuebersichtStyles)
    Div({ classes(UrlaubsuebersichtStyles.page) }) {
        Div({ classes(UrlaubsuebersichtStyles.toolbar) }) {
            Div({ classes(UrlaubsuebersichtStyles.toolbarLeft) }) {
                Div({ classes(UrlaubsuebersichtStyles.modeSwitch) }) {
                    LumaticaButton(
                        label = "Teamansicht",
                        onClick = { overviewMode = "Team" },
                        variant = LumaticaButtonVariant.State,
                        isActive = overviewMode == "Team",
                        heightPx = 32
                    )
                    LumaticaButton(
                        label = "Einzelansicht",
                        onClick = {
                            overviewMode = "Einzel"
                        },
                        variant = LumaticaButtonVariant.State,
                        isActive = overviewMode == "Einzel",
                        heightPx = 32
                    )
                }
            }
            Div({ classes(UrlaubsuebersichtStyles.toolbarActions) }) {
                if (overviewMode == "Einzel") {
                    Div({ classes(UrlaubsuebersichtStyles.userDropdown) }) {
                        AppDropdown(
                            options = users.map { user ->
                                All(key = user.id, label = urlaubsuebersichtUserName(user), aktiv = true)
                            },
                            selectedValue = selectedUserId,
                            onChange = {
                                selectedUserId = it
                                singleDayPanelIso = ""
                            },
                            placeholder = "Mitarbeiter wählen",
                            appearance = AppDropdownAppearance.Surface,
                            menuMode = AppDropdownMenuMode.Overlay
                        )
                    }
                }
                Div({ classes(UrlaubsuebersichtStyles.monthNavigation) }) {
                    if (overviewMode == "Team") {
                        UrlaubsuebersichtNavigationButton("images/left.svg", "Vorheriger Monat") {
                            month = month.shift(-1)
                        }
                        Span({ classes(UrlaubsuebersichtStyles.monthLabel) }) {
                            Text("${urlaubsuebersichtMonthName(month.month)} ${month.year}")
                        }
                        UrlaubsuebersichtNavigationButton("images/right.svg", "Nächster Monat") {
                            month = month.shift(1)
                        }
                    } else {
                        UrlaubsuebersichtNavigationButton("images/left.svg", "Zurück") {
                            singleDate = urlaubsuebersichtShiftYear(singleDate, -1)
                            singleDayPanelIso = ""
                        }
                        Span({ classes(UrlaubsuebersichtStyles.singlePeriodLabel) }) {
                            Text(urlaubsuebersichtTimelineLabel(singleDate))
                        }
                        UrlaubsuebersichtNavigationButton("images/right.svg", "Weiter") {
                            singleDate = urlaubsuebersichtShiftYear(singleDate, 1)
                            singleDayPanelIso = ""
                        }
                    }
                }
            }
            Div({ classes(UrlaubsuebersichtStyles.toolbarRight) }) {}
        }

        if (overviewMode == "Einzel") {
            PersonalKontingentStrip(allowancesByUser[selectedUserId])
        }

        if (message.isNotBlank()) {
            Span({ classes(UrlaubsuebersichtStyles.message) }) { Text(message) }
        }

        if (loading) {
            Div({ classes(UrlaubsuebersichtStyles.loading) }) { Text("Urlaubsdaten werden geladen.") }
        } else if (overviewMode == "Team") {
            val columns = "minmax(260px, 320px) repeat($days, minmax(42px, 1fr))"
            val minimumTableWidth = 260 + (days * 42)
            Div({ classes(UrlaubsuebersichtStyles.tableScroll) }) {
                Div({
                    classes(UrlaubsuebersichtStyles.table)
                    style {
                        property("grid-template-columns", columns)
                        minWidth(minimumTableWidth.px)
                    }
                }) {
                    Div({ classes(UrlaubsuebersichtStyles.identityHeader) }) {
                        Span { Text("Mitarbeiter") }
                        Span({ classes(UrlaubsuebersichtStyles.identityHeaderMeta) }) { Text("Frei / Reserviert / Genommen") }
                    }
                    (1..days).forEach { day ->
                        val date = Date(month.year, month.month - 1, day)
                        val weekend = date.getDay().toInt() in setOf(0, 6)
                        val iso = "$monthPrefix${day.toString().padStart(2, '0')}"
                        val isToday = iso == kalenderTodayIsoDate()
                        Div({
                            classes(UrlaubsuebersichtStyles.dayHeader)
                            if (weekend) classes(UrlaubsuebersichtStyles.weekend)
                            if (isToday) {
                                style {
                                    property("box-shadow", "inset 0 0 0 2px ${UISettings.ACCENT_HEX}")
                                }
                            }
                        }) {
                            Span({ classes(UrlaubsuebersichtStyles.weekday) }) {
                                Text(urlaubsuebersichtWeekday(date.getDay().toInt()))
                            }
                            Span({
                                classes(UrlaubsuebersichtStyles.dayNumber)
                                if (isToday) style { color(UISettings.ACCENT) }
                            }) { Text(day.toString()) }
                        }
                    }

                    users.forEach { user ->
                        val balance = allowancesByUser[user.id]
                        val available = balance?.verfuegbar ?: 0.0
                        val reserved = balance?.urlaubReserviert ?: 0.0
                        val taken = balance?.urlaubGenommen ?: 0.0
                        Div({ classes(UrlaubsuebersichtStyles.identityCell) }) {
                            UserAvatarCircle(
                                user = user,
                                fallbackLabel = urlaubsuebersichtUserName(user),
                                sizePx = 34
                            )
                            Div({ classes(UrlaubsuebersichtStyles.identityText) }) {
                                Span({ classes(UrlaubsuebersichtStyles.userName) }) {
                                    Text(urlaubsuebersichtUserName(user))
                                }
                            }
                            Span({
                                classes(UrlaubsuebersichtStyles.vacationBalance)
                                if (available <= 0.0) {
                                    classes(UrlaubsuebersichtStyles.vacationBalanceEmpty)
                                }
                            }) {
                                Text(
                                    "${urlaubsuebersichtDaysLabel(available)} / " +
                                        "${urlaubsuebersichtDaysLabel(reserved)} / " +
                                        urlaubsuebersichtDaysLabel(taken)
                                )
                            }
                        }
                        (1..days).forEach { day ->
                            val iso = "$monthPrefix${day.toString().padStart(2, '0')}"
                            val cellKey = "${user.id}|$iso"
                            val vacation = vacationsByCell[cellKey]
                            val color = vacation?.let { urlaubsuebersichtVacationColor(it, iso) }
                            val date = Date(month.year, month.month - 1, day)
                            val weekend = date.getDay().toInt() in setOf(0, 6)
                            val saving = savingCell == cellKey
                            val isToday = iso == kalenderTodayIsoDate()
                            Div({
                                classes(UrlaubsuebersichtStyles.dayCell)
                                when {
                                    weekend -> classes(UrlaubsuebersichtStyles.weekend)
                                    color != null -> classes(UrlaubsuebersichtStyles.occupiedCell)
                                    else -> classes(UrlaubsuebersichtStyles.emptyCell)
                                }
                                title(
                                    when {
                                        vacation != null -> kontingentAbsenceReference(vacation, iso).label
                                        weekend -> "Wochenende"
                                        else -> "Urlaubstag direkt genehmigen"
                                    }
                                )
                                if (isToday) {
                                    style {
                                        property("box-shadow", "inset 0 0 0 2px ${UISettings.ACCENT_HEX}")
                                    }
                                }
                                if (color != null) {
                                    style { backgroundColor(Color(color)) }
                                }
                                if (!weekend && vacation == null && !saving) {
                                    onClick {
                                        savingCell = cellKey
                                        message = ""
                                        scope.launch {
                                            val saved = UrlaubsApi.requestVacation(
                                                userId = user.id,
                                                datumVon = iso,
                                                datumBis = iso,
                                                status = ZustandManager.Urlaub.GENEHMIGT
                                            )
                                            if (saved) {
                                                reload()
                                            } else {
                                                message = "Urlaubstag konnte nicht gespeichert werden."
                                            }
                                            savingCell = null
                                        }
                                    }
                                }
                            }) {
                                if (saving) {
                                    Span({ classes(UrlaubsuebersichtStyles.savingMark) })
                                }
                            }
                        }
                    }
                }
            }
        } else {
            UrlaubsuebersichtSingleView(
                user = users.firstOrNull { it.id == selectedUserId },
                allowance = allowancesByUser[selectedUserId],
                vacations = vacations.filter { it.userId == selectedUserId },
                cursorIso = singleDate,
                selectedDayIso = singleDayPanelIso,
                onSelectDate = {
                    singleDayPanelIso = it
                },
                onCloseDay = {
                    singleDayPanelIso = ""
                }
            )
        }
    }
}

@Composable
fun CUSAbwesenheitsantraege(art: String = "CUS") {
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("urlaub"),
        GlobalManager.jsonDomainVersion("user"),
        GlobalManager.jsonDomainVersion("personal"),
        GlobalManager.jsonDomainVersion("zeit")
    )
    val scope = rememberCoroutineScope()
    var users by remember { mutableStateOf<List<All>>(emptyList()) }
    var requests by remember { mutableStateOf<List<All>>(emptyList()) }
    var allowances by remember { mutableStateOf<Map<Pair<String, Int>, Urlaubsanspruch>>(emptyMap()) }
    var requirements by remember { mutableStateOf<Map<String, Abwesenheitsbedarf>>(emptyMap()) }
    var processingId by remember { mutableStateOf<String?>(null) }
    var message by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(true) }

    suspend fun reload() {
        loading = true
        users = UrlaubsApi.loadUsers()
        requests = UrlaubsApi.loadAll()
        val years = requests.mapNotNull { it.datumVon.take(4).toIntOrNull() }.distinct()
        allowances = years.flatMap { year ->
            UrlaubsApi.loadAllowances(year).map { (it.userId to year) to it }
        }.toMap()
        requirements = requests
            .filter { urlaubsuebersichtAbsenceType(it).equals("Freizeitausgleich", ignoreCase = true) }
            .associate { request ->
                request.id to UrlaubsApi.loadAbsenceRequirement(
                    request.userId, request.datumVon, request.datumBis, "Freizeitausgleich"
                )
            }
        loading = false
    }

    LaunchedEffect(refreshVersion) { reload() }
    Style(UrlaubsuebersichtStyles)
    Div({ classes(UrlaubsuebersichtStyles.page) }) {
        if (message.isNotBlank()) Span({ classes(UrlaubsuebersichtStyles.message) }) { Text(message) }
        if (loading) {
            Div({ classes(UrlaubsuebersichtStyles.loading) }) { Text("Abwesenheitsanträge werden geladen.") }
        } else {
            UrlaubsuebersichtRequests(
                vacations = requests.sortedWith(compareBy<All> { it.datumVon }.thenBy { it.userId }),
                usersById = users.associateBy { it.id },
                allowances = allowances,
                requirements = requirements,
                processingId = processingId,
                onSearch = { filters ->
                    scope.launch { requests = UrlaubsApi.searchAllVacationOverview(filters) }
                },
                onUpdateStatus = { request, status ->
                    if (processingId == null) {
                        processingId = request.id
                        scope.launch {
                            if (UrlaubsApi.updateStatus(request.id, status)) reload()
                            else message = "Zustand konnte nicht gespeichert werden."
                            processingId = null
                        }
                    }
                }
            )
        }
    }
}

@Composable
private fun UrlaubsuebersichtRequests(
    vacations: List<All>,
    usersById: Map<String, All>,
    allowances: Map<Pair<String, Int>, Urlaubsanspruch>,
    requirements: Map<String, Abwesenheitsbedarf>,
    processingId: String?,
    onSearch: (Map<String, String>) -> Unit,
    onUpdateStatus: (All, Int) -> Unit
) {
    var selectedVacationId by remember(vacations) { mutableStateOf(vacations.firstOrNull()?.id.orEmpty()) }
    val selectedVacation = vacations.firstOrNull { it.id == selectedVacationId }
    val canDecide = selectedVacation?.zustand in setOf(
        ZustandManager.Urlaub.BEANTRAGT,
        ZustandManager.Urlaub.IN_BEARBEITUNG
    ) && processingId == null
    val headers = listOf(
        "MA",
        "Art",
        "Zustand",
        "Beantragt am",
        "Zeitraum",
        "Tage",
        "Urlaubstage verfügbar",
        "Zeitkonto",
        "Zeitreserve (nicht verfügbar)",
        "Reservierte Überstunden",
        "Zeitkonto verfügbar",
        "Freizeitausgleich in Tage",
        "Genehmigt von",
        "Genehmigt am",
        "Abgelehnt von",
        "Abgelehnt am"
    )
    val data = vacations.map { vacation ->
        val user = usersById[vacation.userId]
        val allowance = allowances[vacation.userId to (vacation.datumVon.take(4).toIntOrNull() ?: 0)]
        val requirement = requirements[vacation.id]
        vacation.id to listOf(
            user?.let(::urlaubsuebersichtUserName).orEmpty().ifBlank { vacation.userName.ifBlank { vacation.userId } },
            urlaubsuebersichtAbsenceType(vacation),
            urlaubsuebersichtStatusText(vacation),
            urlaubsuebersichtRequestDateTime(vacation),
            "${formatUrlaubsuebersichtDate(vacation.datumVon)} - ${formatUrlaubsuebersichtDate(vacation.datumBis)}",
            vacation.anzahlTage.toString(),
            urlaubsuebersichtDaysLabel(allowance?.verfuegbar ?: 0.0),
            allowance?.stundenkonto?.let(::urlaubsuebersichtHoursLabel) ?: "-",
            requirement?.takeIf { it.ok }?.zeitreserve?.let(::urlaubsuebersichtHoursLabel) ?: "-",
            requirement?.takeIf { it.ok }?.reservierteUeberstunden?.let(::urlaubsuebersichtHoursLabel) ?: "-",
            requirement?.takeIf { it.ok }?.zeitkontoVerfuegbar?.let(::urlaubsuebersichtHoursLabel) ?: "-",
            requirement?.takeIf { it.ok }?.moeglicheTage?.toString() ?: "-",
            if (vacation.zustand == ZustandManager.Urlaub.GENEHMIGT) urlaubsuebersichtDecisionActor(vacation) else "",
            if (vacation.zustand == ZustandManager.Urlaub.GENEHMIGT) urlaubsuebersichtDecisionDate(vacation) else "",
            if (vacation.zustand == ZustandManager.Urlaub.VERWEIGERT) urlaubsuebersichtDecisionActor(vacation) else "",
            if (vacation.zustand == ZustandManager.Urlaub.VERWEIGERT) urlaubsuebersichtDecisionDate(vacation) else ""
        )
    }

    TabularScreen(
        screenKey = "Urlaubsuebersicht_Antraege",
        rollenKey = "Urlaubsuebersicht",
        title = "Abwesenheitsanträge",
        headers = headers,
        data = data,
        models = vacations,
        editable = false,
        hardReadOnly = false,
        includeMetadata = false,
        allowCreate = false,
        allowSave = false,
        allowDelete = false,
        allowImportButton = true,
        allowExportButton = true,
        allowSettingsButton = true,
        allowRefreshButton = true,
        allowSearchButton = true,
        onSearchWithFilters = onSearch,
        showToolbarExtras = true,
        dataColumnWidthCss = "minmax(150px, 1fr)",
        selectedRowIndices = vacations.indexOfFirst { it.id == selectedVacationId }
            .takeIf { it >= 0 }
            ?.let { setOf(it) }
            ?: emptySet(),
        onRowClick = { rowId -> selectedVacationId = rowId },
        extraNavButtons = listOf(
            TabularToolbarButton(
                label = if (processingId == selectedVacationId) "..." else "Genehmigen",
                icon = "images/check_circle.svg",
                enabled = canDecide,
                onClick = { selectedVacation?.let { onUpdateStatus(it, ZustandManager.Urlaub.GENEHMIGT) } }
            ),
            TabularToolbarButton(
                label = if (processingId == selectedVacationId) "..." else "Ablehnen",
                icon = "images/close.svg",
                enabled = canDecide,
                onClick = { selectedVacation?.let { onUpdateStatus(it, ZustandManager.Urlaub.VERWEIGERT) } }
            )
        ),
        onCommit = { }
    )
}

private fun urlaubsuebersichtRequestDateTime(vacation: All): String =
    listOf(vacation.erstelltAm, vacation.erstelltUm)
        .filter { it.isNotBlank() }
        .joinToString(" ")
        .ifBlank { formatUrlaubsuebersichtDate(vacation.datumVon) }

private fun urlaubsuebersichtAbsenceType(vacation: All): String =
    kontingentAbsenceType(vacation)

private fun urlaubsuebersichtStatusText(vacation: All): String =
    kontingentAbsenceReference(
        vacation,
        vacation.datumBis.ifBlank { kontingentAbsenceDisplayDays(vacation).lastOrNull().orEmpty() }
    ).status

private fun urlaubsuebersichtConsumesVacation(vacation: All): Boolean =
    urlaubsuebersichtAbsenceType(vacation).equals("Urlaub", ignoreCase = true)

private fun urlaubsuebersichtIsVisibleAbsence(vacation: All): Boolean =
    vacation.urlaubstag != false &&
        !urlaubsuebersichtAbsenceType(vacation).equals("Arbeitstag", ignoreCase = true)

private fun urlaubsuebersichtDecisionLabel(vacation: All): String? =
    when (vacation.zustand) {
        ZustandManager.Urlaub.GENEHMIGT -> "Genehmigt von"
        ZustandManager.Urlaub.VERWEIGERT -> "Abgelehnt von"
        else -> null
    }

private fun urlaubsuebersichtDecisionActor(vacation: All): String =
    vacation.geaendertVon.ifBlank { vacation.bearbeitetVon }.ifBlank { vacation.erstelltVon }.ifBlank { "-" }

private fun urlaubsuebersichtDecisionDate(vacation: All): String =
    listOf(vacation.geaendertAm.ifBlank { vacation.bearbeitetAm }, vacation.geaendertUm.ifBlank { vacation.bearbeitetUm })
        .filter { it.isNotBlank() }
        .joinToString(" ")
        .ifBlank { "-" }

private fun urlaubsuebersichtHoursLabel(value: Double): String =
    "${urlaubsuebersichtDaysLabel(value)} Std."

@Composable
private fun UrlaubsuebersichtSingleView(
    user: All?,
    allowance: Urlaubsanspruch?,
    vacations: List<All>,
    cursorIso: String,
    selectedDayIso: String,
    onSelectDate: (String) -> Unit,
    onCloseDay: () -> Unit
) {
    if (user == null) {
        return
    }
    val vacationsByDay = remember(vacations) {
        val result = mutableMapOf<String, All>()
        vacations.forEach { vacation ->
            urlaubsuebersichtDisplayDays(vacation).forEach { day ->
                if (urlaubsuebersichtVacationColor(vacation, day) == null) return@forEach
                val existing = result[day]
                if (existing == null ||
                    kontingentAbsenceStatusPriority(vacation) > kontingentAbsenceStatusPriority(existing)
                ) {
                    result[day] = vacation
                }
            }
        }
        result
    }
    val months = urlaubsuebersichtTimelineMonths(cursorIso)
    val calendarSlots = 31
    val columns = "minmax(190px, 220px) repeat($calendarSlots, minmax(42px, 1fr))"

    Div({ classes(UrlaubsuebersichtStyles.singleView) }) {
        Div({ classes(UrlaubsuebersichtStyles.tableScroll) }) {
            Div({
                classes(
                    UrlaubsuebersichtStyles.table,
                    UrlaubsuebersichtStyles.timelineTable
                )
                style {
                    property("grid-template-columns", columns)
                    minWidth((190 + calendarSlots * 42).px)
                }
            }) {
                months.forEach { rowMonth ->
                    val prefix = "${rowMonth.year}-${rowMonth.month.toString().padStart(2, '0')}-"
                    val daysInMonth = urlaubsuebersichtDaysInMonth(rowMonth.year, rowMonth.month)
                    val isCurrentMonth = rowMonth.year == Date().getFullYear().toInt() &&
                        rowMonth.month == Date().getMonth().toInt() + 1
                    val vacationDays = vacations
                        .flatMap { it.tage }
                        .distinct()
                        .count { it.startsWith(prefix) }
                    Div({
                        classes(
                            UrlaubsuebersichtStyles.identityCell,
                            UrlaubsuebersichtStyles.timelineIdentityCell
                        )
                    }) {
                        Div({ classes(UrlaubsuebersichtStyles.monthIdentityText) }) {
                            Span({
                                classes(UrlaubsuebersichtStyles.userName)
                                if (isCurrentMonth) {
                                    classes(UrlaubsuebersichtStyles.currentMonthName)
                                }
                            }) {
                                Text(urlaubsuebersichtMonthShortName(rowMonth.month))
                            }
                            Span({ classes(UrlaubsuebersichtStyles.monthVacationCount) }) {
                                Text(if (vacationDays == 1) "1 Tag" else "$vacationDays Tage")
                            }
                        }
                    }
                    (1..calendarSlots).forEach { day ->
                        if (day > daysInMonth) {
                            Div({ classes(UrlaubsuebersichtStyles.emptyDayCell) })
                        } else {
                            val iso = "$prefix${day.toString().padStart(2, '0')}"
                            val vacation = vacationsByDay[iso]
                            val color = vacation?.let { urlaubsuebersichtVacationColor(it, iso) }
                            val date = urlaubsuebersichtParseDate(iso)
                            val jsWeekday = date.getDay().toInt()
                            val weekend = jsWeekday in setOf(0, 6)
                            val nonWorkday = jsWeekday == 0 || urlaubsuebersichtIsHoliday(iso)
                            Div({
                                classes(
                                    UrlaubsuebersichtStyles.dayCell,
                                    UrlaubsuebersichtStyles.timelineDayCell
                                )
                                if (nonWorkday) classes(UrlaubsuebersichtStyles.nonWorkdayText)
                                title(
                                    vacation?.let { kontingentAbsenceReference(it, iso).label }
                                        ?: if (weekend) "Wochenende" else "Keine Abwesenheit"
                                )
                                style {
                                    if (color != null) {
                                        backgroundColor(Color(color))
                                    }
                                }
                                onClick { onSelectDate(iso) }
                            }) {
                                Div({ classes(UrlaubsuebersichtStyles.timelineDayContent) }) {
                                    Span({ classes(UrlaubsuebersichtStyles.timelineCellWeekday) }) {
                                        Text(urlaubsuebersichtCellWeekday(jsWeekday))
                                    }
                                    Span({ classes(UrlaubsuebersichtStyles.timelineDayNumber) }) {
                                        Text(day.toString())
                                    }
                                }
                            }
                        }
                    }
                }
                Div({
                    classes(
                        UrlaubsuebersichtStyles.identityCell,
                        UrlaubsuebersichtStyles.timelineIdentityCell,
                        UrlaubsuebersichtStyles.yearSummaryCell
                    )
                }) {
                    Div({ classes(UrlaubsuebersichtStyles.yearSummaryContent) }) {
                        Span({ classes(UrlaubsuebersichtStyles.yearSummaryLabel) }) {
                            Text("Jahres\u00fcbersicht")
                        }
                        Span({ classes(UrlaubsuebersichtStyles.yearSummaryMeta) }) {
                            Text("Genommen / Reserviert / Frei")
                        }
                        Span({ classes(UrlaubsuebersichtStyles.yearSummaryValue) }) {
                            Text(
                                "${urlaubsuebersichtDaysLabel(allowance?.urlaubGenommen ?: 0.0)} / " +
                                    "${urlaubsuebersichtDaysLabel(allowance?.urlaubReserviert ?: 0.0)} / " +
                                    "${urlaubsuebersichtDaysLabel(allowance?.verfuegbar ?: 0.0)} Tage"
                            )
                        }
                    }
                }
                Div({
                    style {
                        property("grid-column", "2 / -1")
                        minWidth(0.px)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                    }
                }) {
                    PersonalKontingentLegend(includeTimeStates = false, singleLine = true)
                }
            }
        }
        if (selectedDayIso.isNotBlank()) {
            UrlaubsuebersichtDayPopup(
                iso = selectedDayIso,
                vacation = vacationsByDay[selectedDayIso],
                onClose = onCloseDay,
                onShiftDate = { delta ->
                    onSelectDate(
                        urlaubsuebersichtIso(
                            urlaubsuebersichtAddDays(urlaubsuebersichtParseDate(selectedDayIso), delta)
                        )
                    )
                }
            )
        }
    }
}

@Composable
internal fun UrlaubsuebersichtDayPopup(
    iso: String,
    vacation: All?,
    onClose: () -> Unit,
    onShiftDate: (Int) -> Unit
) {
    Style(UrlaubsuebersichtStyles)
    Div({ classes(UrlaubsuebersichtStyles.popupOverlay) }) {
        Div({ classes(UrlaubsuebersichtStyles.popupBox) }) {
            Div({ classes(UrlaubsuebersichtStyles.popupHeader) }) {
                Button(attrs = {
                    classes(UrlaubsuebersichtStyles.closeButton)
                    title("Schließen")
                    onClick { onClose() }
                }) { Text("x") }
            }
            Div({ classes(UrlaubsuebersichtStyles.popupNavRow) }) {
                UrlaubsuebersichtNavigationButton("images/left.svg", "Vorheriger Tag") {
                    onShiftDate(-1)
                }
                H3({ classes(UrlaubsuebersichtStyles.popupTitle) }) {
                    Text(formatUrlaubsuebersichtDate(iso))
                }
                UrlaubsuebersichtNavigationButton("images/right.svg", "Nächster Tag") {
                    onShiftDate(1)
                }
            }
            Div({ classes(UrlaubsuebersichtStyles.popupTable) }) {
                val rows = vacation?.let { kontingentAbsenceReference(it, iso).rows }
                    ?: listOf("Zustand" to "Keine Abwesenheit")
                rows.forEach { row -> UrlaubsuebersichtPopupRow(row.first, row.second) }
            }
        }
    }
}

@Composable
private fun UrlaubsuebersichtPopupRow(label: String, value: String) {
    Div({ classes(UrlaubsuebersichtStyles.popupTableRow) }) {
        Span({ classes(UrlaubsuebersichtStyles.popupTableCell) }) { Text(label) }
        Span({ classes(UrlaubsuebersichtStyles.popupTableValue) }) { Text(value) }
    }
}

@Composable
private fun UrlaubsuebersichtNavigationButton(icon: String, tooltip: String, onClick: () -> Unit) {
    Button(attrs = {
        classes(UrlaubsuebersichtStyles.navButton)
        title(tooltip)
        onClick { onClick() }
    }) {
        Img(src = icon, attrs = { classes(UrlaubsuebersichtStyles.navIcon) })
    }
}

private fun urlaubsuebersichtStatusPriority(status: Int): Int =
    when (ZustandManager.Urlaub.fromCode(status)) {
        ZustandManager.Urlaub.STORNIERT -> 40
        ZustandManager.Urlaub.VERWEIGERT -> 35
        ZustandManager.Urlaub.GENEHMIGT -> 30
        ZustandManager.Urlaub.IN_BEARBEITUNG -> 20
        ZustandManager.Urlaub.BEANTRAGT -> 10
        else -> 0
    }

private fun urlaubsuebersichtDisplayDays(vacation: All): List<String> =
    kontingentAbsenceDisplayDays(vacation)

private fun urlaubsuebersichtVacationColor(vacation: All, day: String): String? =
    kontingentAbsenceReference(vacation, day).colorHex

private fun urlaubsuebersichtAbsenceTypeColor(vacation: All): String? =
    when (urlaubsuebersichtAbsenceType(vacation).trim().lowercase()) {
        "freizeitausgleich" -> ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FREIZEITAUSGLEICH)
        "unbezahlt frei" -> ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.FEHLTAG)
        "sonderurlaub" -> ZustandManager.Zeit.calendarColorHexFor(ZustandManager.Zeit.URLAUB)
        else -> null
    }

private fun currentUrlaubsuebersichtMonth(): UrlaubsuebersichtMonth {
    val parsed = parseKalenderIsoDate(kalenderTodayIsoDate())
    return UrlaubsuebersichtMonth(
        year = parsed?.year ?: Date().getFullYear().toInt(),
        month = parsed?.month ?: Date().getMonth().toInt() + 1
    )
}

private fun urlaubsuebersichtParseDate(iso: String): Date {
    val parts = iso.split("-")
    val year = parts.getOrNull(0)?.toIntOrNull() ?: Date().getFullYear().toInt()
    val month = parts.getOrNull(1)?.toIntOrNull() ?: Date().getMonth().toInt() + 1
    val day = parts.getOrNull(2)?.toIntOrNull() ?: 1
    return Date(year, month - 1, day)
}

private fun urlaubsuebersichtIso(date: Date): String =
    "${date.getFullYear().toInt().toString().padStart(4, '0')}-" +
        "${(date.getMonth().toInt() + 1).toString().padStart(2, '0')}-" +
        date.getDate().toInt().toString().padStart(2, '0')

private fun urlaubsuebersichtAddDays(date: Date, days: Int): Date =
    Date(date.getFullYear().toInt(), date.getMonth().toInt(), date.getDate().toInt() + days)

private fun urlaubsuebersichtShiftYear(iso: String, delta: Int): String {
    val date = urlaubsuebersichtParseDate(iso)
    val shifted = Date(date.getFullYear().toInt() + delta, date.getMonth().toInt(), 1)
    return urlaubsuebersichtIso(shifted)
}

private fun urlaubsuebersichtTimelineLabel(iso: String): String {
    val date = urlaubsuebersichtParseDate(iso)
    return date.getFullYear().toInt().toString()
}

private fun urlaubsuebersichtTimelineMonths(anchorIso: String): List<UrlaubsuebersichtMonth> {
    val anchor = urlaubsuebersichtParseDate(anchorIso)
    val selectedYear = anchor.getFullYear().toInt()
    return (1..12).map { month ->
        UrlaubsuebersichtMonth(year = selectedYear, month = month)
    }
}

private fun urlaubsuebersichtDaysInMonth(year: Int, month: Int): Int =
    Date(year, month, 0).getDate().toInt()

private fun urlaubsuebersichtIsHoliday(iso: String): Boolean =
    iso.takeLast(5) in setOf("01-01", "05-01", "10-03", "12-25", "12-26")

private fun urlaubsuebersichtCellWeekday(jsDay: Int): String =
    listOf("SO", "MO", "DI", "MI", "DO", "FR", "SA").getOrElse(jsDay) { "" }

private fun formatUrlaubsuebersichtDate(iso: String): String {
    val parts = iso.split("-")
    if (parts.size < 3) return iso
    return "${parts[2]}.${parts[1]}.${parts[0]}"
}

private fun urlaubsuebersichtUserName(user: All): String =
    user.name.ifBlank {
        listOf(user.vorname, user.nachname).filter { it.isNotBlank() }.joinToString(" ")
    }.ifBlank { user.id }

private fun urlaubsuebersichtDaysLabel(value: Double): String =
    if (value % 1.0 == 0.0) value.toInt().toString() else value.toString().replace(".", ",")

private fun urlaubsuebersichtWeekday(jsDay: Int): String =
    listOf("So", "Mo", "Di", "Mi", "Do", "Fr", "Sa").getOrElse(jsDay) { "" }

private fun urlaubsuebersichtMonthName(month: Int): String =
    listOf(
        "Januar", "Februar", "März", "April", "Mai", "Juni",
        "Juli", "August", "September", "Oktober", "November", "Dezember"
    ).getOrElse(month - 1) { month.toString() }

private fun urlaubsuebersichtMonthShortName(month: Int): String =
    listOf(
        "Jan.", "Feb.", "Mär.", "Apr.", "Mai", "Jun.",
        "Jul.", "Aug.", "Sep.", "Okt.", "Nov.", "Dez."
    ).getOrElse(month - 1) { month.toString() }

private object UrlaubsuebersichtStyles : StyleSheet() {
    val page by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        padding(18.px)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(8.px)
        color(Color.white)
    }
    val toolbar by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "1fr auto 1fr")
        alignItems(AlignItems.Center)
        gap(14.px)
        paddingBottom(4.px)
    }
    val toolbarLeft by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.FlexStart)
        minWidth(0.px)
    }
    val toolbarActions by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(14.px)
        flexWrap(FlexWrap.Wrap)
        minWidth(0.px)
    }
    val toolbarRight by style {
        minWidth(0.px)
    }
    val modeSwitch by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(6.px)
    }
    val singleControls by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.FlexEnd)
        gap(14.px)
        flexWrap(FlexWrap.Wrap)
    }
    val userDropdown by style {
        width(220.px)
        minWidth(180.px)
    }
    val singlePeriodLabel by style {
        minWidth(150.px)
        textAlign("center")
        fontSize(15.px)
        fontWeight("800")
        property("white-space", "nowrap")
    }
    val title by style {
        margin(0.px)
        fontSize(20.px)
        fontWeight("900")
    }
    val subtitle by style {
        display(DisplayStyle.Block)
        marginTop(3.px)
        color(rgba(255, 255, 255, 0.54))
        fontSize(12.px)
    }
    val monthNavigation by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(8.px)
    }
    val monthLabel by style {
        minWidth(150.px)
        textAlign("center")
        fontSize(15.px)
        fontWeight("800")
    }
    val navButton by style {
        width(32.px)
        height(32.px)
        padding(0.px)
        borderRadius(6.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        backgroundColor(rgba(255, 255, 255, 0.06))
        cursor("pointer")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
    }
    val navIcon by style {
        width(13.px)
        height(13.px)
        property("filter", "brightness(0) invert(1)")
    }
    val tableScroll by style {
        minHeight(0.px)
        flexGrow(1)
        overflow("auto")
        backgroundColor(Color.transparent)
    }
    val table by style {
        width(100.percent)
        display(DisplayStyle.Grid)
        property("grid-auto-rows", "60px")
    }
    val timelineTable by style {
        property("grid-auto-rows", "72px")
    }
    val identityHeader by style {
        position(Position.Sticky)
        left(0.px)
        top(0.px)
        property("z-index", "4")
        padding(0.px, 12.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        backgroundColor(Color("#20242a"))
        property("border-right", "1px solid rgba(255,255,255,0.14)")
        property("border-bottom", "1px solid rgba(255,255,255,0.14)")
        fontSize(15.px)
        fontWeight("800")
        boxSizing("border-box")
    }
    val identityHeaderMeta by style {
        color(rgba(255, 255, 255, 0.48))
        fontSize(13.px)
        fontWeight("700")
    }
    val dayHeader by style {
        position(Position.Sticky)
        top(0.px)
        property("z-index", "3")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(2.px)
        backgroundColor(Color("#20242a"))
        property("border-right", "1px solid rgba(255,255,255,0.08)")
        property("border-bottom", "1px solid rgba(255,255,255,0.14)")
        boxSizing("border-box")
    }
    val weekday by style {
        color(rgba(255, 255, 255, 0.44))
        fontSize(13.px)
        fontWeight("700")
    }
    val dayNumber by style {
        fontSize(16.px)
        fontWeight("900")
    }
    val identityCell by style {
        position(Position.Sticky)
        left(0.px)
        property("z-index", "2")
        padding(0.px, 10.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(9.px)
        backgroundColor(Color("#191d22"))
        property("border-right", "2px solid rgba(255,255,255,0.16)")
        property("border-bottom", "1px solid rgba(255,255,255,0.06)")
        boxSizing("border-box")
    }
    val identityText by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(2.px)
        property("flex", "1 1 auto")
    }
    val userName by style {
        maxWidth(205.px)
        overflow("hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
        fontSize(15.px)
        fontWeight("800")
    }
    val vacationBalance by style {
        property("margin-left", "auto")
        color(Color("#84e1a7"))
        fontSize(15.px)
        fontWeight("900")
        property("white-space", "nowrap")
    }
    val vacationBalanceEmpty by style {
        color(UISettings.ACCENT)
    }
    val dayCell by style {
        position(Position.Relative)
        property("border-right", "2px solid rgba(255,255,255,0.11)")
        property("border-bottom", "1px solid rgba(255,255,255,0.05)")
        boxSizing("border-box")
    }
    val emptyCell by style {
        cursor("pointer")
        self + ":hover" style {
            backgroundColor(rgba(255, 255, 255, 0.08))
            property("box-shadow", "inset 0 0 0 1px rgba(255,255,255,0.20)")
        }
    }
    val occupiedCell by style {
        cursor("default")
    }
    val dayStatusDot by style {
        position(Position.Absolute)
        left(50.percent)
        top(50.percent)
        width(12.px)
        height(12.px)
        borderRadius(50.percent)
        property("transform", "translate(-50%, -50%)")
        property("box-shadow", "0 0 9px currentColor")
    }
    val missingBookingDot by style {
        position(Position.Absolute)
        left(50.percent)
        top(50.percent)
        width(17.px)
        height(17.px)
        borderRadius(50.percent)
        property("transform", "translate(-50%, -50%)")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        fontSize(12.px)
        fontWeight("900")
        lineHeight("1")
        property("box-shadow", "0 0 8px currentColor")
    }
    val weekend by style {
        backgroundColor(rgba(255, 255, 255, 0.035))
        color(rgba(255, 255, 255, 0.35))
    }
    val savingMark by style {
        position(Position.Absolute)
        left(50.percent)
        top(50.percent)
        width(8.px)
        height(8.px)
        marginLeft((-4).px)
        marginTop((-4).px)
        borderRadius(50.percent)
        backgroundColor(Color.white)
        property("opacity", "0.8")
    }
    val loading by style {
        padding(16.px)
        color(rgba(255, 255, 255, 0.68))
    }
    val singleView by style {
        minHeight(0.px)
        flexGrow(1)
        overflow("hidden")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
    }
    val monthIdentityText by style {
        width(100.percent)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(10.px)
        minWidth(0.px)
    }
    val timelineIdentityCell by style {
        padding(0.px, 10.px)
        property("border-right", "1px solid rgba(255,255,255,0.18)")
        property("border-bottom", "2px solid rgba(255,255,255,0.48)")
        property("background-clip", "padding-box")
    }
    val monthVacationCount by style {
        property("margin-left", "auto")
        color(rgba(255, 255, 255, 0.54))
        fontSize(13.px)
        fontWeight("800")
        property("white-space", "nowrap")
    }
    val currentMonthName by style {
        color(UISettings.ACCENT)
    }
    val yearSummaryCell by style {
        position(Position.Sticky)
        left(0.px)
        minHeight(72.px)
        backgroundColor(Color("#20242a"))
    }
    val yearSummaryContent by style {
        width(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        textAlign("center")
        gap(3.px)
    }
    val yearSummaryLabel by style {
        color(rgba(255, 255, 255, 0.62))
        fontSize(12.px)
        fontWeight("800")
    }
    val yearSummaryMeta by style {
        color(rgba(255, 255, 255, 0.46))
        fontSize(9.px)
        fontWeight("700")
        property("white-space", "nowrap")
    }
    val yearSummaryValue by style {
        color(Color.white)
        fontSize(13.px)
        fontWeight("900")
        property("white-space", "nowrap")
    }
    val timelineDayCell by style {
        cursor("pointer")
        property("border-right", "1px solid rgba(255,255,255,0.18)")
        property("border-bottom", "2px solid rgba(255,255,255,0.48)")
        property("background-clip", "padding-box")
        self + ":hover" style {
            backgroundColor(rgba(255, 255, 255, 0.08))
            property("box-shadow", "inset 0 0 0 1px rgba(255,255,255,0.24)")
        }
    }
    val timelineDayContent by style {
        width(100.percent)
        height(100.percent)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(3.px)
    }
    val timelineCellWeekday by style {
        fontSize(11.px)
        fontWeight("900")
        lineHeight("1")
        property("opacity", "0.68")
    }
    val timelineDayNumber by style {
        fontSize(15.px)
        fontWeight("900")
        lineHeight("1")
    }
    val nonWorkdayText by style {
        color(UISettings.ACCENT)
    }
    val emptyDayCell by style {
        position(Position.Relative)
        backgroundColor(rgba(255, 255, 255, 0.018))
        property("border-right", "1px solid rgba(255,255,255,0.18)")
        property("border-bottom", "2px solid rgba(255,255,255,0.48)")
        property("background-clip", "padding-box")
        boxSizing("border-box")
    }
    val popupOverlay by style {
        position(Position.Fixed)
        left(0.px)
        top(0.px)
        right(0.px)
        bottom(0.px)
        property("z-index", "10000")
        backgroundColor(rgba(0, 0, 0, 0.52))
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        padding(18.px)
        boxSizing("border-box")
    }
    val popupBox by style {
        width(460.px)
        minHeight(280.px)
        maxWidth(92.vw)
        maxHeight(92.vh)
        overflow("auto")
        padding(18.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.14))
        borderRadius(6.px)
        backgroundColor(Color("#191d22"))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        boxSizing("border-box")
    }
    val popupHeader by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.FlexEnd)
        height(32.px)
    }
    val popupNavRow by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "32px 1fr 32px")
        alignItems(AlignItems.Center)
        gap(10.px)
        minHeight(36.px)
    }
    val popupTitle by style {
        margin(0.px)
        fontSize(15.px)
        fontWeight("900")
        property("white-space", "nowrap")
        textAlign("center")
    }
    val closeButton by style {
        width(32.px)
        height(32.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        borderRadius(5.px)
        backgroundColor(rgba(255, 255, 255, 0.06))
        color(Color.white)
        cursor("pointer")
    }
    val popupTable by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "1fr 1fr")
        minHeight(150.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(5.px)
        overflow("hidden")
    }
    val popupTableRow by style {
        display(DisplayStyle.Contents)
    }
    val popupTableCell by style {
        padding(9.px, 12.px)
        color(rgba(255, 255, 255, 0.62))
        fontSize(15.px)
        fontWeight("800")
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
        backgroundColor(rgba(255, 255, 255, 0.03))
        property("white-space", "nowrap")
    }
    val popupTableValue by style {
        padding(9.px, 12.px)
        color(Color.white)
        fontSize(15.px)
        fontWeight("900")
        textAlign("right")
        property("border-bottom", "1px solid rgba(255,255,255,0.08)")
        backgroundColor(rgba(255, 255, 255, 0.02))
        property("white-space", "nowrap")
    }
    val requestList by style {
        minHeight(0.px)
        flexGrow(1)
        overflow("auto")
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(auto-fit, minmax(420px, 1fr))")
        gap(14.px)
        alignContent(AlignContent.Start)
        padding(2.px)
        boxSizing("border-box")
    }
    val requestCard by style {
        padding(14.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.12))
        borderRadius(6.px)
        backgroundColor(rgba(255, 255, 255, 0.035))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        boxSizing("border-box")
    }
    val requestCardHeader by style {
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.SpaceBetween)
        gap(10.px)
    }
    val requestUser by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        gap(9.px)
    }
    val requestUserText by style {
        minWidth(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(2.px)
    }
    val requestPersonnelNumber by style {
        color(rgba(255, 255, 255, 0.44))
        fontSize(13.px)
    }
    val requestState by style {
        fontSize(15.px)
        fontWeight("800")
    }
    val requestMetrics by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(auto-fit, minmax(150px, 1fr))")
        gap(8.px)
    }
    val requestMetric by style {
        minWidth(0.px)
        padding(10.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.10))
        borderRadius(5.px)
        backgroundColor(rgba(0, 0, 0, 0.14))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(4.px)
        boxSizing("border-box")
    }
    val requestMetricLabel by style {
        color(rgba(255, 255, 255, 0.52))
        fontSize(13.px)
        fontWeight("800")
    }
    val requestMetricValue by style {
        color(Color.white)
        fontSize(15.px)
        fontWeight("900")
        overflow("hidden")
        property("text-overflow", "ellipsis")
        property("white-space", "nowrap")
    }
    val requestActions by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        alignItems(AlignItems.Center)
        gap(6.px)
        flexWrap(FlexWrap.Wrap)
    }
    val processing by style {
        width(16.px)
        color(rgba(255, 255, 255, 0.62))
        fontSize(15.px)
        textAlign("center")
    }
    val message by style {
        color(UISettings.ACCENT)
        fontSize(12.px)
        fontWeight("700")
    }
}
